import React from 'react';
import PropTypes, { bool } from 'prop-types';
import Media from 'react-media';
import { injectIntl, intlShape } from 'react-intl';
import { withRouter } from 'react-router-dom';
import withHook from '@fenderdigital/custom-hooks/withHook';
import useFenderConnect from '@fenderdigital/custom-hooks/hooks/useFenderConnect';
import config from '../../config';
import {
  getTextContainerClassName,
  getWrapperClassName,
  getHeight,
  getTopPosition,
  routeMatch,
  goToSignUp,
  goToPlans,
  getTopOffset,
  shouldHidePagesBanner,
  shouldBindEvent,
} from '../../utils/conversion-banner-utils';
import ConversionBannerText from './ConversionBannerText';
import ConversionBannerLink from './ConversionBannerLink';
import messages from './messages';

class ConversionBanner extends React.PureComponent {
  binded = false;

  static propTypes = {
    intl: intlShape.isRequired,
    subscriptions: PropTypes.shape({}),
    location: PropTypes.shape({
      pathname: PropTypes.string,
    }),
    loggedIn: PropTypes.bool,
  };

  static defaultProps = {
    subscriptions: null,
    loggedIn: false,
    location: {
      pathname: '',
    },
  };

  state = {
    isFixed: false,
  };

  componentDidMount() {
    const { location, subscriptions } = this.props;

    if (shouldBindEvent(location?.pathname, subscriptions?.play, true)) {
      this.bindHandleScroll();
    }
  }

  componentDidUpdate() {
    const { location, subscriptions, loggedIn } = this.props;
    if (shouldBindEvent(location?.pathname, subscriptions?.play, loggedIn) && !this.binded) {
      this.bindHandleScroll();
    } else if (!shouldBindEvent(location?.pathname, subscriptions?.play, loggedIn) && this.binded) {
      this.unBindHandleScroll();
    }
  }

  componentWillUnmount() {
    if (this.binded) {
      this.unBindHandleScroll();
    }
  }

  bindHandleScroll = () => {
    window.addEventListener('scroll', this.handleScroll);

    this.binded = true;
  };

  unBindHandleScroll = () => {
    window.removeEventListener('scroll', this.handleScroll);

    this.binded = false;
  };

  handleScroll = () => {
    const { location, loggedIn, subscriptions } = this.props;
    const { pathname } = location || {};
    const windowY = window.pageYOffset;
    const topOffset = getTopOffset(pathname, loggedIn, subscriptions?.play);
    const routeMatched = routeMatch(pathname || '');
    const { isFixed } = this.state;

    if (topOffset === 0 && !isFixed && !routeMatched && subscriptions?.play === 'active') {
      this.setState({ isFixed: true });
    } else if (topOffset === 0 && isFixed && !routeMatched && subscriptions?.play === 'active') {
      return;
    }

    if (windowY > topOffset && !isFixed) {
      this.setState({ isFixed: true });
    } else if ((windowY < topOffset && isFixed) || (windowY === 0 && isFixed)) {
      this.setState({ isFixed: false });
    }
  };

  renderBanner = matches => {
    const { intl, location, loggedIn } = this.props;
    const { isFixed } = this.state;
    const { formatMessage } = intl;

    if (!routeMatch(location?.pathname) || loggedIn) {
      return null;
    }

    const isFixedMatches = isFixed && matches;
    const wrapperClassName = getWrapperClassName(matches, isFixed);
    const textContainerClassName = getTextContainerClassName(isFixedMatches);
    const height = getHeight(isFixed, matches);

    return (
      <div style={{ height }} data-id="conversion-banner">
        <div style={{ top: 64 }} className={wrapperClassName}>
          <div className={textContainerClassName}>
            <ConversionBannerText
              isFixedMatches={isFixedMatches}
              message={formatMessage(messages.off)}
            />
            <div className="ph3-ns">
              <ConversionBannerLink
                onClick={goToSignUp}
                isFixedMatches={isFixedMatches}
                message={formatMessage(messages.button)}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  renderPagesBanner = (isLarge, isMedium) => {
    const { loggedIn, location, subscriptions } = this.props;
    const { formatMessage } = this.props.intl;
    const { isFixed } = this.state;
    const conditions = {
      subscription: subscriptions?.play,
      loggedIn,
      pathname: location?.pathname,
      isFixed,
      isLarge,
    };

    if (shouldHidePagesBanner(conditions)) {
      return null;
    }

    let buttonMsg = '';
    let onClickAction = null;

    if (!loggedIn || (loggedIn && subscriptions?.play === 'pending')) {
      buttonMsg = formatMessage(messages.button);
      onClickAction = goToSignUp;
    } else if (loggedIn && subscriptions?.play === 'cancelled') {
      buttonMsg = formatMessage(messages.cancelled);
      onClickAction = goToPlans;
    }

    const isAuthFixed = loggedIn && isFixed;
    const top = getTopOffset(location?.pathname, isAuthFixed, subscriptions?.play);
    const wrapperStyle = isAuthFixed ? { height: isMedium ? 97 : 62 } : null;

    return (
      <div role="banner" data-id="conversion-banner" style={wrapperStyle}>
        <div style={{ top }} className="bg-black w-100 pa3 fixed z-5">
          <div className="flex flex-column items-center justify-center tc white futura-heavy">
            <div className="f5 mb2 lh-title dn db-m">{formatMessage(messages.off)}</div>
            <a
              href={`${process.env.REACT_APP_CLIENT_SSO_URL}/sign-up?ref=${process.env.REACT_APP_CLIENT_FENDER_COM}/play/plans?session-started=true`}
              className="link dib br2 ttu bg-red futura-heavy lh-solid white f6-m ph3-m pa2 f8 ba bn-m b--dark-red"
              onClick={onClickAction}
            >
              {buttonMsg}
            </a>
          </div>
        </div>
      </div>
    );
  };

  render() {
    return (
      <>
        <Media query={config.breakpoints.small}>{this.renderBanner}</Media>
        <Media query={config.breakpoints.large}>
          {matches => (matches ? this.renderPagesBanner(true, false, false) : null)}
        </Media>
        <Media query={config.breakpoints.medium}>
          {matches => (matches ? this.renderPagesBanner(false, true, false) : null)}
        </Media>
        <Media query={config.breakpoints.small}>
          {matches => (matches ? this.renderPagesBanner(false, false, true) : null)}
        </Media>
      </>
    );
  }
}

export default injectIntl(withRouter(withHook(ConversionBanner, useFenderConnect, [])));
